import React, { useCallback } from "react";
import { BorrowerFunnelCardHeader } from "./BorrowerFunnelCardHeader";
import { BorrowerFunnelCardBody } from "./BorrowerFunnelCardBody";
import { BorrowerFunnelCardFooter } from "./BorrowerFunnelCardFooter";
import { BorrowerContractFunnelV2Dto, ComparateurPricingStatus } from "api/pricing";
import { AlertTriangle, X } from "lucide-react";
import { useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { pricingAPI } from "services/swagger-api";

type Props = {
  isProMode: boolean,
  contractFunnel: BorrowerContractFunnelV2Dto,
};

const SimpleTag = ({ percentage }: { percentage: number }) => {
  return (
    <svg width="150" height="50" viewBox="0 0 150 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Tag shape */}
      <path
        d="M8 5 L130 5 L145 25 L130 45 L8 45 C5 45 3 43 3 40 L3 10 C3 7 5 5 8 5 Z"
        className="fill-secondary stroke-secondary"
        strokeWidth="1.5"
      />
      {/* Text OFFRE MULTI-ASSURÉS */}
      <text
        x="70"
        y="20"
        className="fill-white text-[9px] font-semibold uppercase"
        textAnchor="middle"
        style={{ letterSpacing: '0.3px' }}
      >
        OFFRE MULTI-ASSURÉS
      </text>
      {/* Percentage */}
      <text
        x="70"
        y="37"
        className="fill-white text-[18px] font-bold"
        textAnchor="middle"
      >
        -{percentage}%
      </text>
    </svg>
  );
};

const DiscountTagLoader = () => (
  <div className="relative w-[150px] h-[50px]">
    <svg width="150" height="50" viewBox="0 0 150 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 5 L130 5 L145 25 L130 45 L8 45 C5 45 3 43 3 40 L3 10 C3 7 5 5 8 5 Z"
        className="fill-secondary stroke-secondary"
        strokeWidth="1.5"
      />
      <text
        x="70"
        y="20"
        className="fill-white text-[9px] font-semibold uppercase"
        textAnchor="middle"
        style={{ letterSpacing: '0.3px' }}
      >
        OFFRE MULTI-ASSURÉS
      </text>
    </svg>
    <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-[3px]">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce [animation-delay:0ms]" />
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce [animation-delay:150ms]" />
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce [animation-delay:300ms]" />
    </span>
  </div>
);

export const BorrowerFunnelCard: React.FC<Props> = ({
  isProMode,
  contractFunnel,
}) => {
  const { adjustmentsConfig } = useMainBorrowerPricingState();

  const isPricingLoading = contractFunnel.pricingStatus === ComparateurPricingStatus.Pending
    || contractFunnel.pricingStatus === ComparateurPricingStatus.Loading

  const hasError = contractFunnel.pricingStatus === ComparateurPricingStatus.Error
    || contractFunnel.pricingStatus === ComparateurPricingStatus.Timeout
    || !!contractFunnel.errorMessage

  const discountPercentage = contractFunnel.pricing?.discount?.discountPercentage

  // Mode édition : déterminé par le back
  const isEditing = contractFunnel.isEdition ?? false

  // Remettre les fees du funnel aux valeurs globales via appel API
  const handleResetEdition = useCallback(async () => {
    const globalFees = adjustmentsConfig.fees?.map(f => ({ code: f.code, value: f.value })) ?? []
    await pricingAPI.customizeBorrowerContractFunnelV2({
      oldHash: contractFunnel.hash,
      customBorrowerContractFunnelCommandDto: {
        fees: globalFees,
      }
    })
  }, [contractFunnel.hash, adjustmentsConfig.fees])

  if (!contractFunnel) {
    return <></>
  }

  return (
    <div
      className={`relative rounded-xl shadow w-full ${isEditing ? 'border-2 border-dashed border-secondary' : 'border'} ${hasError ? 'bg-gray-50/80 grayscale-[30%]' : 'bg-white'}`}
    >
      {isEditing && (
        <div className="absolute flex items-center gap-1 -top-3 left-1/2 transform -translate-x-1/2 bg-secondary text-white px-2 py-1 rounded-full text-xs font-medium z-10">
          ÉDITION
          <button className="w-4 h-4 cursor-pointer hover:opacity-70" onClick={handleResetEdition}>
            <X className="w-full h-full" />
          </button>
        </div>
      )}
      {
        !hasError && contractFunnel.borrowers.length > 1 &&
        <div className="absolute -top-3 -left-3 drop-shadow-lg">
          {!isPricingLoading && discountPercentage
            ? <SimpleTag percentage={discountPercentage} />
            : <DiscountTagLoader />
          }
        </div>
      }
      <div className="flex w-full border-b">
        <BorrowerFunnelCardHeader
          logoURL={contractFunnel.product.logoURL}
          productId={contractFunnel.product.id}
          title={contractFunnel.product && contractFunnel.product.label}
        />
        <BorrowerFunnelCardBody
          contractFunnel={contractFunnel}
          isProMode={isProMode}
          hasError={hasError}
          errorMessage={contractFunnel.errorMessage}
        />
        {hasError ? (
          <div className="flex items-center justify-center flex-col gap-2 py-4 px-6 flex-none min-w-[200px]">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-50">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-sm font-semibold text-red-600">Tarification indisponible</p>
            </div>
          </div>
        ) : (
          <BorrowerFunnelCardFooter contractFunnel={contractFunnel} />
        )}
      </div>
      {!hasError && contractFunnel.warningMessages && contractFunnel.warningMessages.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 rounded-b-xl border-t border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <div className="flex flex-col gap-0.5">
            {contractFunnel.warningMessages.map((msg, i) => (
              <p key={i} className="text-xs text-amber-700">{msg}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
